import { SystemEntity, ReferenceType } from '@erp/shared-types';
import { Badge } from '@/components/ui/badge';
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from '@/components/ui/hover-card';
import { cn } from '@/lib/utils';
import {
  ShoppingCartIcon,
  PackageIcon,
  TruckIcon,
  AirplaneTiltIcon,
  FileTextIcon,
  LinkIcon,
  PaperclipIcon,
  Icon,
} from '@phosphor-icons/react';
import { SystemEntityPreview } from '../preview-ui';
import { useExternalSystemReference } from '@/api/rpc/hooks/core/useExternalSystemReferences';

const REFERENCE_TYPE_ICONS: Record<ReferenceType, Icon> = {
  [ReferenceType.PurchaseOrder]: ShoppingCartIcon,
  [ReferenceType.SalesOrder]: PackageIcon,
  [ReferenceType.TransferOrder]: PackageIcon,
  [ReferenceType.Invoice]: FileTextIcon,
  [ReferenceType.BillOfLading]: TruckIcon,
  [ReferenceType.Shipment]: AirplaneTiltIcon,
  [ReferenceType.ProofOfDelivery]: TruckIcon,
  [ReferenceType.Other]: LinkIcon,
};

const REFERENCE_TYPE_LABELS: Record<ReferenceType, string> = {
  [ReferenceType.PurchaseOrder]: 'PO',
  [ReferenceType.SalesOrder]: 'SO',
  [ReferenceType.TransferOrder]: 'TO',
  [ReferenceType.Invoice]: 'INV',
  [ReferenceType.BillOfLading]: 'BOL',
  [ReferenceType.Shipment]: 'SHIP',
  [ReferenceType.ProofOfDelivery]: 'POD',
  [ReferenceType.Other]: 'REF',
};

interface ExternalSystemReferenceInlineProps {
  entityType: SystemEntity;
  entityId: string;
  className?: string;
}

export function ExternalSystemReferenceInline({
  entityId,
  className,
}: ExternalSystemReferenceInlineProps) {
  const { data, isLoading } = useExternalSystemReference(entityId);

  const RefIcon = data?.referenceType
    ? REFERENCE_TYPE_ICONS[data.referenceType]
    : LinkIcon;

  const label = data?.referenceType
    ? REFERENCE_TYPE_LABELS[data.referenceType]
    : 'REF';

  const displayName = isLoading
    ? 'Loading...'
    : data
      ? `${label} ${data.referenceValue}`
      : 'Reference';

  return (
    <HoverCard openDelay={200} closeDelay={100}>
      <HoverCardTrigger asChild>
        <div className="inline-block max-w-full">
          <Badge
            variant="outline"
            className={cn('gap-1.5 cursor-pointer max-w-full', className)}
          >
            <PaperclipIcon className="size-3 shrink-0 text-muted-foreground" />
            <RefIcon className="size-3 shrink-0" />
            <span className="truncate">{displayName}</span>
          </Badge>
        </div>
      </HoverCardTrigger>
      <HoverCardContent
        className="w-auto p-0 pointer-events-auto"
        align="start"
        side="bottom"
        sideOffset={2}
        onPointerEnter={(e) => e.stopPropagation()}
        onPointerLeave={(e) => e.stopPropagation()}
      >
        <SystemEntityPreview
          entityType={SystemEntity.EXTERNAL_SYSTEM_REFERENCE}
          entityId={entityId}
        />
      </HoverCardContent>
    </HoverCard>
  );
}
